import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLinkIcon, XIcon, ArrowLeftIcon } from 'lucide-react';
import { useMusic } from '../lib/cmsStore';
import { MusicItem } from '../lib/cmsTypes';
// Inline music detail modal
function MusicModal({
  item,
  onClose
}: {
  item: MusicItem;
  onClose: () => void;
}) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);
  const tags = [item.genre, ...(item.mood || []), ...(item.tags || [])].filter(Boolean).flatMap((t: string) => t.includes(',') ? t.split(',').map((s: string) => s.trim()).filter(Boolean) : [t]).filter((v: string, i: number, a: string[]) => a.indexOf(v) === i);
  const links = item.streamingLinks?.filter(l => l.url) || [];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.35,
    ease: [0.22, 1, 0.36, 1]
  }} className="fixed inset-0 z-50 bg-background overflow-y-auto" data-id="element-221">
      <button onClick={onClose} className="fixed top-6 right-6 md:top-8 md:right-12 z-50 p-2 text-muted-foreground hover:text-foreground transition-colors duration-200" aria-label="Close" data-id="element-222">
        <XIcon className="w-6 h-6" data-id="element-223" />
      </button>

      <div className="min-h-screen flex flex-col" data-id="element-224">
        <div className="flex-1 w-full max-w-[960px] mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-8" data-id="element-225">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="flex items-center gap-2 mb-8 md:mb-10" data-id="element-226">
            <span className="font-mono text-xs text-primary tracking-[0.2em] uppercase" data-id="element-227">
              // Music
            </span>
            {item.genre && <>
                <span className="font-mono text-xs text-muted-foreground" data-id="element-228">
                  {'>'}
                </span>
                <span className="font-mono text-xs text-muted-foreground tracking-widest uppercase" data-id="element-229">
                  {item.genre}
                </span>
              </>}
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="font-display text-[clamp(2.5rem,8vw,7rem)] font-bold leading-[0.9] tracking-tighter mb-8 md:mb-10 text-foreground" data-id="element-230">
            {item.title}
          </motion.h1>

          {item.subtitle && <motion.p initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.25
        }} className="font-mono text-lg text-muted-foreground mb-8" data-id="element-231">
              {item.subtitle}
            </motion.p>}

          {tags.length > 0 && <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="flex flex-wrap gap-3 mb-16 md:mb-20" data-id="element-232">
              {tags.map(tag => <span key={tag} className="font-mono text-xs px-4 py-2 border border-border text-muted-foreground" data-id="element-233">
                  {tag}
                </span>)}
            </motion.div>}

          {item.coverArtwork && <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.35
        }} className="mb-16" data-id="element-234">
              <img src={item.coverArtwork} alt={item.title} className="w-full max-w-md aspect-square object-cover border border-border" data-id="element-235" />
            </motion.div>}

          {item.description && <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.4
        }} className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4 md:gap-16 py-10 md:py-14" data-id="element-236">
              <h3 className="font-mono text-xs text-primary uppercase tracking-[0.2em] font-medium pt-1" data-id="element-237">
                About
              </h3>
              <p className="font-mono text-sm md:text-base text-muted-foreground leading-relaxed max-w-2xl whitespace-pre-line" data-id="element-238">
                {item.description}
              </p>
            </motion.div>}

          {item.credits && <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.45
        }} className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4 md:gap-16 py-10 md:py-14 border-t border-border" data-id="element-239">
              <h3 className="font-mono text-xs text-primary uppercase tracking-[0.2em] font-medium pt-1" data-id="element-240">
                Credits
              </h3>
              <p className="font-mono text-sm text-muted-foreground leading-relaxed max-w-2xl whitespace-pre-line" data-id="element-241">
                {item.credits}
              </p>
            </motion.div>}

          {item.audioEmbed && <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.5
        }} className="py-10 border-t border-border" data-id="element-242">
              <h3 className="font-mono text-xs text-primary uppercase tracking-[0.2em] font-medium mb-6" data-id="element-243">
                Listen
              </h3>
              <div dangerouslySetInnerHTML={{
            __html: item.audioEmbed
          }} data-id="element-244" />
            </motion.div>}

          {links.length > 0 && <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.55
        }} className="py-10 border-t border-border" data-id="element-245">
              <h3 className="font-mono text-xs text-primary uppercase tracking-[0.2em] font-medium mb-6" data-id="element-246">
                Stream
              </h3>
              <div className="flex flex-wrap gap-4" data-id="element-247">
                {links.map(link => <a key={link.platform} href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors border-b border-transparent hover:border-primary pb-0.5" data-id="element-248">
                    {link.platform}
                    <ExternalLinkIcon className="w-3 h-3" data-id="element-249" />
                  </a>)}
              </div>
            </motion.div>}

          <div className="border-t border-border" data-id="element-250" />

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.6
        }} className="flex items-center justify-between py-8 md:py-10" data-id="element-251">
            <button onClick={onClose} className="group flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground transition-colors duration-300" aria-label="Back to music" data-id="element-252">
              <ArrowLeftIcon className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-300" data-id="element-253" />
              Back to Music
            </button>
          </motion.div>
        </div>
      </div>
    </motion.div>;
}
interface MusicSectionProps {
  initialSlug?: string;
}
export function MusicSection({
  initialSlug
}: MusicSectionProps) {
  const {
    items,
    isLoading
  } = useMusic();
  const [selectedMusic, setSelectedMusic] = useState<MusicItem | null>(null);
  const hasOpenedRef = useRef(false);
  const publishedMusic = items.filter(item => item.status === 'published');
  const sortedMusic = [...publishedMusic].sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    return (a.order || 0) - (b.order || 0);
  });
  // Auto-open modal for deep link
  useEffect(() => {
    if (initialSlug && !isLoading && items.length > 0 && !hasOpenedRef.current) {
      const found = items.find(m => m.slug === initialSlug || m.id === initialSlug);
      if (found) {
        setSelectedMusic(found);
        hasOpenedRef.current = true;
      }
    }
  }, [initialSlug, isLoading, items]);
  if (isLoading) return null;
  if (sortedMusic.length === 0) return null;
  return <>
      <section id="music" className="py-24 md:py-40 px-6 md:px-12 border-t border-border" data-id="element-254">
        <div className="max-w-6xl mx-auto" data-id="element-255">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.6
        }} data-id="element-256">
            <span className="font-mono text-xs text-primary uppercase tracking-widest" data-id="element-257">
              // Music
            </span>
            <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tighter mt-4" data-id="element-258">
              Music
            </h2>

            <div className="mt-16 space-y-0" data-id="element-259">
              {sortedMusic.map((item, i) => {
              const tags = [item.genre, ...(item.mood || []), ...(item.tags || [])].filter(Boolean).flatMap((t: string) => t.includes(',') ? t.split(',').map((s: string) => s.trim()).filter(Boolean) : [t]).filter((v: string, idx: number, a: string[]) => a.indexOf(v) === idx);
              const links = item.streamingLinks?.filter((l: any) => l.url) || [];
              return <motion.div key={item.id || item.title} initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-50px'
              }} transition={{
                duration: 0.5,
                delay: i * 0.1
              }} className="border-t border-border py-10 md:py-14 cursor-pointer group" onClick={() => setSelectedMusic(item)} data-id="element-260">
                    <div className="flex flex-col md:flex-row gap-8 md:gap-16" data-id="element-261">
                      {/* Cover art */}
                      {item.coverArtwork && <div className="w-full md:w-48 shrink-0" data-id="element-262">
                          <div className="aspect-square overflow-hidden bg-card" data-id="element-263">
                            <img src={item.coverArtwork} alt={item.title} className="w-full h-full object-cover" data-id="element-264" />
                          </div>
                        </div>}

                      {/* Info */}
                      <div className="flex-1 min-w-0" data-id="element-265">
                        <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight mb-2 group-hover:text-primary transition-colors duration-300" data-id="element-266">
                          {item.title}
                        </h3>

                        {item.subtitle && <p className="font-mono text-sm text-muted-foreground mb-4" data-id="element-267">
                            {item.subtitle}
                          </p>}

                        {item.description && <p className="font-mono text-sm text-muted-foreground leading-relaxed mb-6 max-w-xl whitespace-pre-line" data-id="element-268">
                            {item.description}
                          </p>}

                        {tags.length > 0 && <div className="flex flex-wrap gap-2 mb-6" data-id="element-269">
                            {tags.map(tag => <span key={tag} className="font-mono text-xs px-3 py-1.5 border border-border text-muted-foreground" data-id="element-270">
                                {tag}
                              </span>)}
                          </div>}

                        {links.length > 0 && <div className="flex flex-wrap gap-4" onClick={e => e.stopPropagation()} data-id="element-271">
                            {links.map((link: any) => <a key={link.platform} href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors border-b border-transparent hover:border-primary pb-0.5" data-id="element-272">
                                {link.platform}
                                <ExternalLinkIcon className="w-3 h-3" data-id="element-273" />
                              </a>)}
                          </div>}
                      </div>
                    </div>
                  </motion.div>;
            })}
            </div>
          </motion.div>
        </div>
      </section>

      <AnimatePresence data-id="element-274">
        {selectedMusic && <MusicModal item={selectedMusic} onClose={() => setSelectedMusic(null)} data-id="element-275" />}
      </AnimatePresence>
    </>;
}